/**
 * #1106 — request-scoped rules for persisting an inline image (pure).
 *
 * Lives on the data side of the editor boundary with `useInlineImageUpload`.
 * Nothing under `components/richText` imports this: the editor control does not
 * decide whether an upload is allowed.
 */
import {
  INLINE_PHOTO_REJECTED_ERROR,
  isAllowedInlinePhotoFile,
} from '../../constants/inlineImageMimeTypes';
import {
  MAX_UPLOAD_SIZE_BYTES,
  MAX_UPLOAD_SIZE_MB,
  validateFileForSecurity,
} from '../../utils/fileSecurityValidation';

/** Same cap as Supporting Documents / `/attachments` (NFR7). */
export const INLINE_IMAGE_CAP_BYTES = MAX_UPLOAD_SIZE_BYTES;

export function attachmentReference(attachmentId: number): string {
  return `sitescope-attachment://${attachmentId}`;
}

export function assertInlineImageAcceptable(file: {
  name: string;
  size?: number;
  type?: string;
}): { ok: true } | { ok: false; error: string } {
  if (!isAllowedInlinePhotoFile({ name: file.name, type: file.type })) {
    return { ok: false, error: INLINE_PHOTO_REJECTED_ERROR };
  }
  if (
    typeof file.size === 'number' &&
    (file.size <= 0 || file.size > INLINE_IMAGE_CAP_BYTES)
  ) {
    return { ok: false, error: 'Image is invalid or exceeds the size limit' };
  }
  const security = validateFileForSecurity(
    { name: file.name, size: file.size ?? 0, type: file.type },
    MAX_UPLOAD_SIZE_MB,
  );
  if (!security.valid) {
    return { ok: false, error: security.error || 'Image failed validation' };
  }
  return { ok: true };
}

export function hasPersistedRequestId(requestId: number | null): boolean {
  return requestId != null && Number.isFinite(requestId) && requestId > 0;
}

/** Stage locally when offline or the request has no id yet (create flow). */
export function shouldStageInlineImage(options: {
  isOnline: boolean;
  requestId: number | null;
}): boolean {
  return !options.isOnline || !hasPersistedRequestId(options.requestId);
}

export function canUploadClipboardImage(options: {
  isOnline: boolean;
  requestId: number | null;
}): { ok: true } | { ok: false; reason: string } {
  if (!options.isOnline) {
    return {
      ok: false,
      reason: 'Offline — clipboard image paste is deferred until back online',
    };
  }
  if (!hasPersistedRequestId(options.requestId)) {
    return {
      ok: false,
      reason: 'Save the request before adding inline images',
    };
  }
  return { ok: true };
}
